import managerTeachersApi from '@/api/manage-teachers';

const actions = {
  FETCH_TEACHERS: async ({ commit, dispatch, state, rootGetters }) => {
    const {
      currentPage,
      pageSize,
      searchKeywords,
      ordering,
      filterByLanguage,
      filterByOrg,
      active,
      is_coach,
      availabilityFilter: { no_availability, no_availability_3week },
    } = state;

    const offset = currentPage * pageSize;
    const params = {
      limit: pageSize,
      offset,
      ordering,
      active,
      is_coach,
      teaching_language: filterByLanguage,
      no_availability: no_availability.value,
      no_availability_3week: no_availability_3week.value,
      organization_id: filterByOrg || rootGetters.organizationId,
      search_query: searchKeywords ? encodeURIComponent(searchKeywords) : null,
    };

    dispatch('wait/start', 'teachers.fetching', { root: true });

    try {
      const { count, results: teachers } = await managerTeachersApi.fetchTeachersList(params);

      commit('SET_TEACHERS', teachers);
      commit('SET_TOTAL_COUNT', count);
      commit('SET_REFRESH_DATE', Date.now());
      dispatch('wait/end', 'teachers.fetching', { root: true });
    } catch (error) {
      if (error.name !== 'AbortError') {
        dispatch('wait/end', 'teachers.fetching', { root: true });
        throw error;
      }
    }
  },

  CHANGE_ORDERING: ({ commit, dispatch, state }, { ordering }) => {
    if (ordering === state.ordering) {
      return;
    }

    commit('SET_ORDERING', ordering);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_TEACHERS');
  },

  CHANGE_PAGE_SIZE: ({ commit, dispatch }, { pageSize }) => {
    commit('SET_PAGE_SIZE', pageSize);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_TEACHERS');
  },

  CHANGE_CURRENT_PAGE: ({ commit, dispatch }, { currentPage }) => {
    commit('SET_CURRENT_PAGE', currentPage);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_KEYWORD: ({ commit, dispatch }) => {
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_ORG: ({ commit, dispatch }, org) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_ORG', org);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_LANGUAGE: ({ commit, dispatch }, language) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_LANGUAGE', language);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_AVAILABILITY: ({ commit, dispatch }, availability) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_AVAILABILITY', availability);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_AVAILABILITY_3_WEEK: ({ commit, dispatch }, availability) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_AVAILABILITY_3_WEEK', availability);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_ACTIVE: ({ commit, dispatch }, active) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_ACTIVE', active);
    dispatch('FETCH_TEACHERS');
  },

  SEARCH_BY_IS_COACH: ({ commit, dispatch }, isCoach) => {
    commit('SET_CURRENT_PAGE', 0);
    commit('SET_IS_COACH', isCoach);
    dispatch('FETCH_TEACHERS');
  },

  FETCH_TEACHER_PROFILE: async ({ commit, dispatch }, teacherHashEmail) => {
    dispatch('wait/start', 'teacherProfile.fetching', { root: true });

    try {
      const teacherProfile = await managerTeachersApi.fetchTeacherProfile(teacherHashEmail);
      commit('SET_TEACHER_PROFILE', teacherProfile);
      dispatch('wait/end', 'teacherProfile.fetching', { root: true });
      return teacherProfile;
    } catch (error) {
      if (error.name !== 'AbortError') {
        dispatch('wait/end', 'teacherProfile.fetching', { root: true });
        throw error;
      }

      return error;
    }
  },

  UPDATE_TEACHER_PROFILE: async ({ commit, dispatch }, { teacherHashEmail, value }) => {
    dispatch('wait/start', 'teacherProfile.isSaving', { root: true });

    try {
      const teacherProfile = await managerTeachersApi.updateTeacherProfile({ teacherHashEmail, value });
      commit('UPDATE_TEACHER_PROFILE', value);
      dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
      return teacherProfile;
    } catch (error) {
      if (error.name !== 'AbortError') {
        dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
        throw error;
      }

      return error;
    }
  },

  UPDATE_TEACHING_OFFERING: async ({ dispatch }, { teacherHashEmail, offeringCode, offered }) => {
    dispatch('wait/start', 'teacherProfile.isSaving', { root: true });

    try {
      // No commit here: the modal's before-close refetches the profile, which is where the
      // offerings are resolved, so the store never carries a stale answer.
      const offering = await managerTeachersApi.updateTeachingOffering({ teacherHashEmail, offeringCode, offered });
      return offering;
    } catch (error) {
      if (error.name !== 'AbortError') {
        throw error;
      }

      return error;
    } finally {
      // In a finally so an abort clears the flag too: the manage-teachers api shares one
      // AbortController across endpoints, so the profile refetch on close can abort a save in
      // flight. It ends its own wait key, not this one, which would leave isSaving stuck true.
      dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
    }
  },

  CREATE_TEACHER_PROFILE: async ({ dispatch }, { value }) => {
    dispatch('wait/start', 'teacherProfile.isSaving', { root: true });

    try {
      const teacherProfile = await managerTeachersApi.createTeacherProfile(value);
      dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
      return teacherProfile;
    } catch (error) {
      if (error.name !== 'AbortError') {
        dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
        throw error;
      }

      return error;
    }
  },

  UPDATE_TEACHER_MEDIA_PROFILE: async ({ dispatch }, { teacherHashEmail, value, mediaType }) => {
    dispatch('wait/start', 'teacherProfile.isSaving', { root: true });

    try {
      const teacherProfile = await managerTeachersApi.updateTeacherMediaProfile({ teacherHashEmail, value, mediaType });
      dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
      return teacherProfile;
    } catch (error) {
      if (error.name !== 'AbortError') {
        dispatch('wait/end', 'teacherProfile.isSaving', { root: true });
        throw error;
      }

      return error;
    }
  },
};

export default actions;
